/**
 * localStorage-backed persistence for orchestrator-sidebar UX state that
 * should survive a page refresh:
 *
 *   - `openTabs[]` and `activeTabId` — so refresh restores the working set
 *     of conversation tabs the user assembled (AXI-3530). Persisted as a
 *     JSON array of conversation ids plus the focused id.
 *   - `sidebarWidth` — so the user's drag-resized sidebar stays the size
 *     they left it at.
 *   - `activeConversationId` (legacy) — single-tab predecessor of
 *     `activeTabId`. Read only by the migration helper so users who land on
 *     the new code mid-session get their one open conversation rehydrated
 *     as a single open tab.
 *   - Thinking levels — remembered independently for each provider/model.
 *   - Ask/Agent composer mode — last-used is recorded when the user
 *     toggles, but a *new* conversation always starts in Ask. A capped
 *     per-conversation map keeps an existing thread in the mode it was
 *     left in across reloads and rail ↔ page handoffs.
 *
 * Mirrors the read/write style used by `ontology-branch` and
 * `ontology-api-key` in `api/ontology-api.ts`.
 */

import { MODEL_EFFORT_LEVEL_OPTIONS } from '../api/model-policy/types'
import type {
  OrchestratorEffortSelection,
  OrchestratorModelSelection
} from '../slices/agent-orchestrator-slice'

const STORAGE_KEY_OPEN_TAB_IDS = 'ontology.open-tab-ids'
const STORAGE_KEY_ACTIVE_TAB_ID = 'ontology.active-tab-id'
const STORAGE_KEY_SIDEBAR_WIDTH = 'ontology.sidebar-width'
const STORAGE_KEY_LEGACY_ACTIVE_CONVERSATION = 'ontology.active-conversation-id'
const STORAGE_KEY_THINKING_LEVELS = 'ontology.chat-thinking-levels'
const STORAGE_KEY_CHAT_COMPOSER_MODE = 'ontology.chat-composer-mode'
const STORAGE_KEY_CHAT_COMPOSER_MODES = 'ontology.chat-composer-modes'
/** Oldest-first prune so the per-conversation map cannot grow without bound. */
export const MAX_CHAT_COMPOSER_MODE_ENTRIES = 50
const DEFAULT_THINKING_LEVEL: OrchestratorEffortSelection = 'medium'
const VALID_THINKING_LEVELS = new Set(MODEL_EFFORT_LEVEL_OPTIONS.map((option) => option.value))

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && 'localStorage' in window
}

function thinkingModelKey(selection: OrchestratorModelSelection): string {
  return `${selection.provider}/${selection.model}`
}

function getPersistedThinkingLevels(): Record<string, OrchestratorEffortSelection> {
  if (!hasLocalStorage()) return {}
  const raw = window.localStorage.getItem(STORAGE_KEY_THINKING_LEVELS)
  if (raw === null) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, OrchestratorEffortSelection] =>
          typeof entry[1] === 'string' &&
          VALID_THINKING_LEVELS.has(entry[1] as OrchestratorEffortSelection)
      )
    )
  } catch {
    return {}
  }
}

/** Returns the remembered per-model thinking level, defaulting to Medium. */
export function getThinkingLevelPreference(
  selection: OrchestratorModelSelection
): OrchestratorEffortSelection {
  return getPersistedThinkingLevels()[thinkingModelKey(selection)] ?? DEFAULT_THINKING_LEVEL
}

/** Persists a thinking level independently for each provider/model pair. */
export function setThinkingLevelPreference(
  selection: OrchestratorModelSelection,
  level: OrchestratorEffortSelection
): void {
  if (!hasLocalStorage()) return
  try {
    const preferences = getPersistedThinkingLevels()
    preferences[thinkingModelKey(selection)] = level
    window.localStorage.setItem(STORAGE_KEY_THINKING_LEVELS, JSON.stringify(preferences))
  } catch {
    // Private browsing and storage quotas should not block the chat composer.
  }
}

// ── Composer Ask/Agent mode ───────────────────────────────────────────

/** The main chat composer's mode dropdown: Ask (read-only) vs Agent (full orchestrator). */
export type ChatComposerMode = 'ask' | 'agent'

function isChatComposerMode(value: unknown): value is ChatComposerMode {
  return value === 'ask' || value === 'agent'
}

/**
 * Last mode the user picked in the composer. Existing threads read from
 * {@link getChatComposerModeForConversation}; a new conversation always
 * starts in Ask and does not use this as a seed.
 */
export function getChatComposerModePreference(): ChatComposerMode {
  if (!hasLocalStorage()) return 'ask'
  const value = window.localStorage.getItem(STORAGE_KEY_CHAT_COMPOSER_MODE)
  return value === 'agent' ? 'agent' : 'ask'
}

export function setChatComposerModePreference(mode: ChatComposerMode): void {
  if (!hasLocalStorage()) return
  try {
    window.localStorage.setItem(STORAGE_KEY_CHAT_COMPOSER_MODE, mode)
  } catch {
    // Private browsing and storage quotas should not block the chat composer.
  }
}

interface StoredComposerModeEntry {
  id: string
  mode: ChatComposerMode
}

function getPersistedComposerModeEntries(): StoredComposerModeEntry[] {
  if (!hasLocalStorage()) return []
  const raw = window.localStorage.getItem(STORAGE_KEY_CHAT_COMPOSER_MODES)
  if (raw === null) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const entries: StoredComposerModeEntry[] = []
    for (const item of parsed) {
      if (typeof item !== 'object' || item === null) continue
      const id = 'id' in item ? item.id : undefined
      const mode = 'mode' in item ? item.mode : undefined
      if (typeof id !== 'string' || id.trim() === '' || !isChatComposerMode(mode)) continue
      entries.push({ id, mode })
    }
    return entries
  } catch {
    return []
  }
}

function setPersistedComposerModeEntries(entries: StoredComposerModeEntry[]): void {
  if (!hasLocalStorage()) return
  try {
    if (entries.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY_CHAT_COMPOSER_MODES)
      return
    }
    window.localStorage.setItem(STORAGE_KEY_CHAT_COMPOSER_MODES, JSON.stringify(entries))
  } catch {
    // Private browsing and storage quotas should not block the chat composer.
  }
}

/** Stored Ask/Agent mode for one conversation, or `null` when unknown. */
export function getChatComposerModeForConversation(id: string): ChatComposerMode | null {
  const entries = getPersistedComposerModeEntries()
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i]!.id === id) return entries[i]!.mode
  }
  return null
}

/**
 * Persist a conversation's composer mode and bump it to most-recent so
 * the oldest entries are the ones pruned when the cap is hit.
 */
export function setChatComposerModeForConversation(id: string, mode: ChatComposerMode): void {
  const trimmed = id.trim()
  if (trimmed === '') return
  const next = getPersistedComposerModeEntries().filter((entry) => entry.id !== trimmed)
  next.push({ id: trimmed, mode })
  while (next.length > MAX_CHAT_COMPOSER_MODE_ENTRIES) next.shift()
  setPersistedComposerModeEntries(next)
}

export function removeChatComposerModeForConversation(id: string): void {
  const current = getPersistedComposerModeEntries()
  const next = current.filter((entry) => entry.id !== id)
  if (next.length === current.length) return
  setPersistedComposerModeEntries(next)
}

// ── Open tabs (AXI-3530) ──────────────────────────────────────────────

export function getPersistedOpenTabIds(): string[] {
  if (!hasLocalStorage()) return []
  const raw = window.localStorage.getItem(STORAGE_KEY_OPEN_TAB_IDS)
  if (raw === null) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Defensive — drop any non-string / blank entries so a corrupted blob
    // can't crash the hydration path.
    return parsed.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
  } catch {
    return []
  }
}

export function setPersistedOpenTabIds(ids: string[]): void {
  if (!hasLocalStorage()) return
  if (ids.length === 0) {
    window.localStorage.removeItem(STORAGE_KEY_OPEN_TAB_IDS)
    return
  }
  window.localStorage.setItem(STORAGE_KEY_OPEN_TAB_IDS, JSON.stringify(ids))
}

export function getPersistedActiveTabId(): string | null {
  if (!hasLocalStorage()) return null
  const value = window.localStorage.getItem(STORAGE_KEY_ACTIVE_TAB_ID)?.trim()
  return value && value.length > 0 ? value : null
}

export function setPersistedActiveTabId(id: string | null): void {
  if (!hasLocalStorage()) return
  if (id === null || id.length === 0) {
    window.localStorage.removeItem(STORAGE_KEY_ACTIVE_TAB_ID)
  } else {
    window.localStorage.setItem(STORAGE_KEY_ACTIVE_TAB_ID, id)
  }
}

// ── Legacy single-tab key (read-only) ─────────────────────────────────

/**
 * Read the single-tab predecessor of `activeTabId`. Only call from the
 * one-shot migration helper below — new code should always use
 * `getPersistedActiveTabId()`.
 */
export function getPersistedActiveConversationId(): string | null {
  if (!hasLocalStorage()) return null
  const value = window.localStorage.getItem(STORAGE_KEY_LEGACY_ACTIVE_CONVERSATION)?.trim()
  return value && value.length > 0 ? value : null
}

/**
 * Kept for symmetry with `getPersistedActiveConversationId` — callers that
 * cleared the legacy key on conversation-switch keep working unchanged.
 */
export function setPersistedActiveConversationId(id: string | null): void {
  if (!hasLocalStorage()) return
  if (id === null || id.length === 0) {
    window.localStorage.removeItem(STORAGE_KEY_LEGACY_ACTIVE_CONVERSATION)
  } else {
    window.localStorage.setItem(STORAGE_KEY_LEGACY_ACTIVE_CONVERSATION, id)
  }
}

/**
 * One-shot migration from the single-tab predecessor. Returns the open-tabs
 * and active-tab values to seed the slice with, then *removes* the legacy
 * key so future calls fall through to the new keys.
 *
 * Resolution order:
 *   1. If the new keys are already populated, return them as-is (no migration needed).
 *   2. Otherwise, if the legacy key holds a value, seed `openTabIds = [legacy]`
 *      and `activeTabId = legacy`; clear the legacy key on the way out.
 *   3. Otherwise, return empty / null (fresh user).
 *
 * Designed to be called once at mount; idempotent across calls because the
 * first invocation writes the new keys (or no-ops) so subsequent calls take
 * the step-1 fast path.
 */
export interface HydratedTabPersistence {
  openTabIds: string[]
  activeTabId: string | null
}

export function hydrateAndMigratePersistedTabs(): HydratedTabPersistence {
  const openTabIds = getPersistedOpenTabIds()
  const activeTabId = getPersistedActiveTabId()
  if (openTabIds.length > 0 || activeTabId !== null) {
    return { openTabIds, activeTabId }
  }
  const legacy = getPersistedActiveConversationId()
  if (legacy === null) {
    return { openTabIds: [], activeTabId: null }
  }
  // Migrate: seed the new keys + clear the legacy one so subsequent reads
  // skip the legacy lookup entirely.
  setPersistedOpenTabIds([legacy])
  setPersistedActiveTabId(legacy)
  setPersistedActiveConversationId(null)
  return { openTabIds: [legacy], activeTabId: legacy }
}

// ── Sidebar width (unchanged) ─────────────────────────────────────────

export function getPersistedSidebarWidth(): number | null {
  if (!hasLocalStorage()) return null
  const raw = window.localStorage.getItem(STORAGE_KEY_SIDEBAR_WIDTH)
  if (raw === null) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export function setPersistedSidebarWidth(width: number): void {
  if (!hasLocalStorage()) return
  window.localStorage.setItem(STORAGE_KEY_SIDEBAR_WIDTH, String(width))
}
